import {
  Autocomplete,
  AutocompleteInputChangeReason,
  SxProps,
} from "@mui/material";
import { HTMLAttributes, SyntheticEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AutocompleteType } from "../../../types/autoComplete";
import CommonTextField from "../commonTextField/CommonTextField";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import AutocompleteChip from "./AutocompleteChip";

type Props = {
  options: AutocompleteType[];
  fullWidth?: boolean;
  onChange: Function;
  name: string;
  onInputChange?: (
    key: string,
    value: string,
    reason?: AutocompleteInputChangeReason,
  ) => void;
  defaultValue?: AutocompleteType[];
  onOpen?: (key: string) => void;
  label?: string;
  isOptionEqualToValue?: (
    option: AutocompleteType,
    value: AutocompleteType,
  ) => boolean;
  disabled?: boolean;
  resetValues?: boolean;
  customInputLabel?: string | null;
  customInputLabelProps?: object;
  textFieldError?: boolean;
  textFieldHelperText?: string;
  isLoading?: boolean;
  getOptionLabel?: (option: AutocompleteType) => string;
  labelLink?: (option: AutocompleteType) => string;
  sx?: SxProps;
  setTagDisabledForRemovedOptions?: boolean;
  readOnly?: boolean;
  // disable client filter when searching from the backend
  disableClientFilter?: boolean;
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: AutocompleteType,
  ) => React.ReactNode;
};

const CustomAutocompleteMulti = ({
  options,
  fullWidth,
  onChange,
  name,
  onInputChange,
  defaultValue = [],
  onOpen,
  label,
  isOptionEqualToValue,
  disabled,
  resetValues,
  customInputLabel,
  customInputLabelProps,
  textFieldError,
  textFieldHelperText,
  isLoading,
  getOptionLabel,
  labelLink,
  setTagDisabledForRemovedOptions,
  sx,
  readOnly = false,
  disableClientFilter = false,
  renderOption,
}: Props) => {
  const [autocompleteValues, setAutocompleteValues] = useState(defaultValue);

  useEffect(() => {
    setAutocompleteValues(defaultValue);
  }, [resetValues, defaultValue]);

  const getInputLabel = () => {
    if (customInputLabel) return customInputLabel;
    return !!autocompleteValues.length ? null : label;
  };

  return (
    <Autocomplete
      loading={isLoading}
      loadingText={<LoadingSpinner />}
      sx={sx}
      disabled={disabled}
      readOnly={readOnly}
      multiple
      fullWidth={fullWidth}
      options={options}
      filterOptions={disableClientFilter ? (opts) => opts : undefined}
      value={autocompleteValues.sort((a, b) => {
        const nameA = a.name || a.id; // Fallback to id if name is undefined
        const nameB = b.name || b.id; // Fallback to id if name is undefined
        return nameA.localeCompare(nameB);
      })}
      disableClearable
      disableCloseOnSelect
      onOpen={() => {
        if (onOpen) {
          onOpen(name);
        }
      }}
      isOptionEqualToValue={
        isOptionEqualToValue
          ? (option, value) => isOptionEqualToValue(option, value)
          : (option, value) => option.name === value.name
      }
      getOptionLabel={getOptionLabel ? getOptionLabel : (option) => option.name}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...rest } = getTagProps({ index });
          const disabled =
            setTagDisabledForRemovedOptions &&
            !options.some((o) => o.id === option.id);
          return (
            <AutocompleteChip
              key={key}
              {...rest}
              label={
                labelLink ? (
                  <Link
                    to={labelLink(option)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: "none",
                      color: "inherit",
                      fontWeight: "bold",
                    }}
                  >
                    {option.name || option.id}
                  </Link>
                ) : (
                  option.name || option.id
                )
              }
              disabled={disabled}
            />
          );
        })
      }
      onChange={(
        e: SyntheticEvent<Element, Event>,
        newValue: AutocompleteType[] | null,
      ) => {
        if (newValue) {
          setAutocompleteValues(newValue);
          onChange(e, newValue, name);
        }
      }}
      onInputChange={(e, value, reason) => {
        if (onInputChange) {
          onInputChange(name, value, reason);
        }
      }}
      renderOption={
        renderOption
          ? renderOption
          : (
              props: React.HTMLAttributes<HTMLLIElement>,
              option: AutocompleteType,
            ) => (
              <li {...props} key={option.id}>
                {option.name || option.id}
                {/* Show as plain text in dropdown */}
              </li>
            )
      }
      renderInput={(params) => (
        <CommonTextField
          {...params}
          name={name}
          label={getInputLabel()}
          InputLabelProps={{ shrink: false, ...customInputLabelProps }}
          error={textFieldError}
          helperText={textFieldHelperText}
        />
      )}
    />
  );
};

export default CustomAutocompleteMulti;
